/*
 * improvemint-cart-recheck.js — re-reading Best Buy on the CHECKOUT CLICK, and
 * telling the shopper in the cart when something moved under them.
 *
 * GP, 2026-09-26:
 *
 *   "When in cart before we checkout. Because users may have an item saved in
 *    cart for a long time. We need to check again and update the shipping
 *    delivery time, and in store pickup availability before checking out and
 *    update user with alert in cart if anything changes."
 *
 * ── WHY THE TRIGGER IS THE CLICK AND NOT THE RENDER ───────────────────────
 * Best Buy asked us not to make per-view lookups, and named availability dates
 * for every item on every site view as their example of misuse
 * (docs/BESTBUY-PARTNER-ANSWERS.md F5). The Commerce bucket is 2 req/s and is
 * SHARED with live order placement (F13). The mini-cart is rendered on every
 * page of this store and the cart drawer re-renders on every quantity change, so
 * a check bound to "the cart is on screen" is precisely the traffic pattern they
 * asked us not to send.
 *
 * A checkout click is the opposite of a view: it happens once per cart, it is a
 * deliberate act, and it is the last moment at which telling the shopper still
 * helps them. So that is the only trigger. Nothing here runs on load, on scroll,
 * on a drawer opening or on a cart re-render.
 *
 * ── FAIL OPEN. ALWAYS. ────────────────────────────────────────────────────
 * A Best Buy outage must never stop a shopper checking out, so this acts only on
 * what it positively knows:
 *
 *   - a rejected fetch, a non-2xx, a body carrying `error`, a SKU we ran out of
 *     time to ask about, storage that throws, JS that never loaded: the click
 *     goes through untouched;
 *   - the whole check is bounded by ONE deadline from the click (DEADLINE_MS).
 *     When it expires we stop asking and proceed with whatever landed. The
 *     in-flight requests still finish and still warm the cache, so a second
 *     click is faster rather than slower;
 *   - only the first click is ever held, and only when something actually
 *     changed. A cart where nothing moved sees a busy button for as long as the
 *     lookup takes and then goes straight to checkout.
 *
 * Four things do not fail open: `unsellable: true`; (2026-10-17) a cart whose
 * one fulfilment cannot carry a line Best Buy only home-delivers;
 * (2026-10-27) a cart whose one fulfilment cannot carry a line Best Buy only
 * hands over at a store; and (2026-10-28) a line with no route at all to the
 * cart's ZIP, from the endpoint's explicit `no-stores-near-zip` — see below. The second is decided from the answer when
 * there is one and, when there is not, from the product's `bb-delivery:home` tag,
 * which is local, Best Buy's own word, and costs no call — the same footing as
 * the ZIP check. The third is decided from the answer ONLY: there is no tag for
 * it, so a line with no answer, or an `error` body, is never that finding.
 * *This said "Two things do not fail open" until 2026-10-27, and "Three" until
 * 2026-10-28.*
 *
 * ── THE CALL BUDGET ───────────────────────────────────────────────────────
 * One /apps/bestbuy/options?sku=&zip= per distinct Best Buy SKU in the cart,
 * run in SERIES, capped at MAX_SKUS, under the shared deadline.
 *
 * Single-SKU mode rather than the batch `?skus=` the downgrade check uses, and
 * the difference matters three ways:
 *
 *   1. the batch answer carries no `unsellable`. Worse, its server-side
 *      Promise.all rejects if ANY sku 4xxs — which is exactly what an unsellable
 *      SKU does — so one bad line degrades the whole cart's answer to "unknown".
 *      The 13.1% G21 population is the headline risk here and the batch cannot
 *      see it at all;
 *   2. the batch takes no ZIP (`cartEligibility` in availability.ts), so it can
 *      only answer "pickup-eligible anywhere", never "at the store this shopper
 *      chose". The single call WITH the ZIP returns `pickup.stores[]`, which is
 *      the only way to check the chosen store. docs/COMMERCE-PDP-PICKER.md §8
 *      names that as the gap, and says narrowing it "would need a per-SKU call
 *      with the ZIP, which is the per-view traffic Best Buy asked us not to
 *      send". It is not per-view here. That is the whole reason this is
 *      affordable on a click and would not be on a render;
 *   3. `delivery.earliestDate` is only on the single answer, and it is what
 *      says whether the delivery estimate moved. It costs no extra Best Buy
 *      call: /commerce/productservice already carries the dates.
 *
 * Cost of that choice, stated plainly: single mode is ~2 Commerce calls per SKU
 * server-side (productavailability + productservice) against the batch's 1, plus
 * one Stores API call per ZIP on the OTHER bucket. All three are cached 60 s
 * server-side across every shopper.
 *
 * What keeps it small:
 *   - we read improvemint-pickup.js's OWN sessionStorage cache first, on its own
 *     keys (`im_bb_o:<sku>:<zip>`, 10 min). A shopper who reached the cart from
 *     the product page usually pays nothing at all, and what we do fetch is
 *     written back for the PDP to reuse;
 *   - the verdict is cached per cart signature for VERDICT_TTL_MS, so clicking
 *     Checkout twice, or coming back to the cart, costs nothing;
 *   - an incomplete verdict is never cached — one timeout must not silence the
 *     check for ten minutes;
 *   - MAX_SKUS caps a pathological cart. Lines past it are "unknown", which by
 *     the rule above can only ever let a click through.
 *
 * ── WHAT THE SHOPPER SEES, BY SEVERITY ────────────────────────────────────
 *
 *   block  `unsellable: true` — Best Buy refuses the SKU, or offers no route to
 *          it at all. Checkout is refused for as long as that line is in the
 *          cart, and the alert names the product and asks them to remove it.
 *          This was the ONE non-fail-open case until 2026-10-17 (the truck-only
 *          block below is the second, the pickup-only block of 2026-10-27 the
 *          third), and it is deliberately the
 *          endpoint's own narrow verdict — never inferred from `pickup: null` +
 *          `delivery: null`, never from a card state, never from an error. Same
 *          rule, same flag and the same reasoning as the Add to Cart block
 *          (COMMERCE-PDP-PICKER.md §3b); this closes that rule's own stated gap,
 *          a quick-add from a grid, plus everything added before it shipped.
 *
 *   block  (2026-10-17) a line Best Buy can only home-deliver — `shipping:
 *          false` with home delivery on the answer, or the `bb-delivery:home`
 *          tag when there is no answer — in a cart that is not booked for home
 *          delivery with a complete window: on `delivery`, blank, an incomplete
 *          window, or store pickup at a store that cannot hand it over. Such an
 *          order goes out as ship-to-home or pickup and fails placement. The
 *          alert names the item and links to its page to choose a date. A MIXED
 *          cart — that line beside a Best Buy item that ships or is picked up —
 *          is blocked too, with a message naming both, because no single
 *          cart-level choice carries both kinds. Blocked, never rewritten: what a
 *          mixed cart should do is open — GP deciding. A truck-only line is never
 *          offered "Switch to shipping" (canShipAll needs `shipping !== false` on
 *          every line).
 *
 *   block  (2026-10-27) a line Best Buy only hands over at a store — the answer
 *          carries a `pickup` and `delivery: null` (or no `delivery`), and is not
 *          `unsellable` — in a cart that is not store pickup at a store that has
 *          it: on `delivery`, blank, any home-delivery window, a pickup with no
 *          store, or pickup at a store the answer's `pickup.stores` does not
 *          list (no `stores` array at all counts as served — fail open). Such an
 *          order goes out as ship-to-home, which Best Buy refuses: placement
 *          FAILED. Examples, both answering `pickup` + `delivery: null` on
 *          2026-10-27: 6351275 ($2,999) and 6506838 ($76.99); an estimated ~240
 *          Available SKUs without home delivery have that shape (95% interval
 *          roughly 70–800). The alert names the item and links to its page to
 *          choose a store. A MIXED cart — that line beside a Best Buy line that
 *          has a delivery route and no pickup, so must ship or come by truck — is
 *          blocked too, naming both, because no single cart-level choice carries
 *          both. A truck-only line is left to the block above: a pickup-only line
 *          is one of its "others", so the truck-only mixed message already names
 *          both, and whenever it fires this one adds nothing — "choose a store"
 *          beside "remove one or the other" would be two answers to one cart.
 *          Acted on only from a real answer: never from no answer, never
 *          from `error`, and never from `pickup: null` + `delivery: null`, which
 *          is the degraded shape and not a verdict (the `unsellable` rule). Never
 *          offered "Switch to shipping": no `delivery`, so canShipAll is false.
 *          Before this the line fell into the store HOLD below at a store that
 *          lacked it — which lets the second click through — and into nothing at
 *          all on a shipping or blank cart.
 *
 *   block  (2026-10-28) nothing can bring a line to THIS shopper: Best Buy was
 *          asked about the cart's ZIP and named no store that has it
 *          (`pickup: null` + `pickupUnavailableReason: "no-stores-near-zip"`,
 *          bestbuy-sync #648) and there is no `delivery`. Whatever the cart
 *          says — no store, cart choice or date can carry it — so the alert asks
 *          them to remove it. Read off that explicit reason only; the same
 *          both-null shape without it is still the degraded no-answer and no
 *          finding. Never `unsellable`: this is about one ZIP, and the item
 *          stays on sale everywhere else. A truck-only line with no store near
 *          the ZIP (GP's 6576999 at 33401) needs nothing new: its pickup is
 *          null, so a pickup cart no longer serves it and the truck-only block
 *          fires.
 *
 *   hold   the chosen pickup store can no longer provide an item (since
 *          2026-10-27 not a pickup-only one — that is the block above). Checkout is
 *          held and the alert offers ONE click out of it: "Switch to shipping".
 *          Held rather than downgraded silently, because a store that ran out is
 *          not the same as an item nobody can collect — another store nearby
 *          probably has it, and picking a different store for the shopper, or
 *          moving them to shipping without asking, is changing their choice for
 *          them. Placement would fail this after they had paid, so letting it
 *          through is not an option either.
 *
 *   info   the estimate moved, or their ZIP is not the one the cart was quoted
 *          for. The first click is held so they READ it in the cart rather than
 *          discovering it at checkout, and the next click goes through.
 *
 * Nothing is written to the cart except by the shopper pressing "Switch to
 * shipping". In particular a moved delivery estimate does NOT rewrite
 * `bb_delivery_date`: the options answer carries a date and no start time, and
 * half a window fails placement outright with DELIVERY_WINDOW_MISSING (§6). The
 * banner already says "Confirmed when your order is placed", so the alert
 * updates the estimate without contradicting it.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DO ──────────────────────────────────────
 * *This said it "does not report a SKU that stopped being shippable and is now
 * home-delivery only", because that population (G10, 7,608 products) was
 * "unfulfillable whatever the cart says until the owner switch is on". The
 * switch — `homeDeliveryEnabled` — has been on since 2026-09-25
 * (docs/COMMERCE-PIPELINE.md), so a home-delivery-only line IS fulfillable, but
 * only on a cart booked for home delivery. Every other cart holding one fails
 * placement, and it was reaching checkout: on 2026-10-17 this file returned no
 * finding for any of the broken carts it was tried on. That case is now a
 * block (above).*
 *
 * It does not intercept accelerated checkout buttons (Shop Pay and friends).
 * They post to Shopify directly from their own iframe, so nothing in the theme
 * can get in front of them. They are off on this store today.
 *
 * No dependencies beyond the one shared writer noted at switchToShipping().
 * Idempotent.
 */
(function () {
  'use strict';
  if (window.__imCartRecheckInit) return;
  window.__imCartRecheckInit = true;

  var ENDPOINT = '/apps/bestbuy/options';

  /*
   * improvemint-pickup.js's own key space, deliberately. It writes
   * `im_bb_o:<sku>:<zip>` as `{t: <ms>, v: <answer>}` and expires at 10 minutes;
   * reading and writing the same shape means the product page's fetch and this
   * one are one cache rather than two, in both directions. Changing either
   * changes both — that is the point.
   */
  var OPT_PREFIX = 'im_bb_o:';
  var OPT_TTL_MS = 10 * 60 * 1000;

  // `2` since 2026-10-17: a clean verdict cached by the old script (up to 10 min)
  // would wave through the truck-only carts this version blocks. `3` since
  // 2026-10-27 for the same reason with the pickup-only carts — and an old
  // cached HOLD for a pickup-only line at the wrong store would let the second
  // click through, where this version blocks.
  var VERDICT_PREFIX = 'im_bb_cart_recheck3:';
  var VERDICT_TTL_MS = 10 * 60 * 1000;
  var ZIP_KEY = 'im_bb_zip';                 // shared with both PDP drawers

  var MAX_SKUS = 10;                         // distinct SKUs we will ask about
  var DEADLINE_MS = 4000;                    // one budget for the whole check
  var REPLAY_AFTER_MS = 6000;                // the button can never be stranded
  var NAMES_SHOWN = 3;                       // then "and N more items"

  /* ---------------------------------------------------------------- storage */

  /* Wrapped like every other storage access in this theme: Safari in private
   * mode throws on write, and a shopper with site data blocked must lose the
   * cache, not the checkout. */
  function read(store, key) {
    try { return store.getItem(key); } catch (e) { return null; }
  }
  function write(store, key, value) {
    try { store.setItem(key, value); } catch (e) { /* non-fatal */ }
  }

  function currentZip() {
    var z = read(localStorage, ZIP_KEY);
    return /^\d{5}$/.test(z || '') ? z : '';
  }

  function optGet(key) {
    try {
      var raw = JSON.parse(read(sessionStorage, OPT_PREFIX + key) || 'null');
      if (!raw || Date.now() - raw.t > OPT_TTL_MS) return null;
      return raw.v;
    } catch (e) { return null; }
  }
  function optSet(key, value) {
    write(sessionStorage, OPT_PREFIX + key, JSON.stringify({ t: Date.now(), v: value }));
  }

  function verdictGet(sig) {
    try {
      var raw = JSON.parse(read(sessionStorage, VERDICT_PREFIX + sig) || 'null');
      if (!raw || Date.now() > raw.e) return null;
      return raw.v;
    } catch (e) { return null; }
  }
  function verdictSet(sig, verdict) {
    write(sessionStorage, VERDICT_PREFIX + sig, JSON.stringify({ e: Date.now() + VERDICT_TTL_MS, v: verdict }));
  }

  /* --------------------------------------------------------------- elements */

  function banners() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-im-cart-fulfillment-banner]'));
  }

  function label(banner, name, fallback) {
    return (banner && banner.getAttribute('data-label-rc-' + name)) || fallback;
  }

  /*
   * The cart's Best Buy lines, from the index improvemint-cart-fulfillment.liquid
   * already renders inside the banner for the downgrade check. One list, two
   * readers — so the two can never disagree about which lines are Best Buy's, and
   * neither can ask about a SKU the product page would not have asked about
   * (`improvemint-bb-sku` is the shared resolver behind it).
   */
  /*
   * `home` is the `bb-delivery:home` product tag and `url` the line's product
   * page (both 2026-10-17): the truck-only block reads the tag when Best Buy gave
   * no answer, and links the item to the page where a delivery date is chosen.
   * The pickup-only block (2026-10-27) uses `url` the same way, for the page
   * where a pickup store is chosen; it has no tag to read.
   */
  function lines(banner) {
    var seen = {};
    return Array.prototype.slice
      .call(banner.querySelectorAll('[data-im-cart-sku-title]'))
      .map(function (el) {
        return {
          sku: el.getAttribute('data-im-cart-sku') || '',
          title: el.getAttribute('data-im-cart-sku-title') || '',
          home: el.getAttribute('data-im-cart-sku-home') === '1',
          url: el.getAttribute('data-im-cart-sku-url') || ''
        };
      })
      .filter(function (l) {
        if (!l.sku || seen[l.sku]) return false;   // qty 2 is still one lookup
        seen[l.sku] = true;
        return true;
      });
  }

  /*
   * What the cart currently says, read from the attributes Liquid mirrored onto
   * the banner — NOT from `data-state`, which is the rendered state and collapses
   * an incomplete choice to "shipping". The re-check wants the raw truth.
   */
  function cartState(banner) {
    function attr(name) { return (banner.getAttribute(name) || '').trim(); }
    var zip = attr('data-im-cart-zip');
    var hdZip = attr('data-im-cart-hd-zip');
    return {
      choice: attr('data-im-cart-choice'),
      storeId: attr('data-im-cart-store-id'),
      storeName: attr('data-im-cart-store-name'),
      hdDate: attr('data-im-cart-hd-date'),
      hdStart: attr('data-im-cart-hd-start'),
      zip: zip,
      hdZip: hdZip,
      // The ZIP the cart was QUOTED at, which is what we must look up with and
      // what the shopper's own ZIP has to be compared against. For a home
      // delivery that is bb_delivery_zip (the one placement checks against the
      // checkout address); otherwise the plain bb_zip.
      quotedZip: hdZip || zip
    };
  }

  /* The cart's identity. Any change to it re-opens the question, and nothing
   * else does — order must not matter, a quantity change must not. */
  function signature(items, cart) {
    return items.map(function (i) { return i.sku; }).sort().join(',')
      + '|' + cart.choice
      + '|' + cart.storeId
      + '|' + cart.hdDate
      + '|' + cart.quotedZip
      + '|' + currentZip();
  }

  /* ----------------------------------------------------------------- fetch */

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  /*
   * Resolve `null` — "we do not know" — when `p` has not settled within `ms`.
   * The request is NOT aborted: it still lands, still writes the cache, and so
   * still pays for the next click. Abandoning it would make a second attempt
   * cost a second Best Buy call.
   */
  function withDeadline(p, ms) {
    return new Promise(function (resolve) {
      var done = false;
      function settle(v) { if (done) return; done = true; clearTimeout(t); resolve(v); }
      var t = setTimeout(function () { settle(null); }, ms);
      p.then(settle, function () { settle(null); });
    });
  }

  function fetchOptions(sku, zip) {
    var key = sku + ':' + (zip || '');
    var hit = optGet(key);
    if (hit) return Promise.resolve(hit);
    var url = ENDPOINT + '?sku=' + encodeURIComponent(sku) + (zip ? '&zip=' + encodeURIComponent(zip) : '');
    return getJSON(url).then(function (data) { optSet(key, data); return data; });
  }

  /*
   * One answer per SKU, in SERIES. Firing them together would hand the Commerce
   * bucket a burst in front of whatever order is being placed at that moment; in
   * series the bucket sees the same total spread out, and the deadline below
   * stops us before a long cart can become a long wait.
   */
  function lookupAll(items, zip, deadlineAt) {
    var answers = {};
    var asked = items.slice(0, MAX_SKUS);
    return asked.reduce(function (chain, item) {
      return chain.then(function () {
        var left = deadlineAt - Date.now();
        if (left <= 0) return null;                    // out of time: the rest stay unknown
        return withDeadline(fetchOptions(item.sku, zip), left).then(function (data) {
          if (data) answers[item.sku] = data;
        });
      });
    }, Promise.resolve()).then(function () { return answers; });
  }

  /* ---------------------------------------------------------------- verdict */

  function validDate(d) { return /^\d{4}-\d{2}-\d{2}$/.test(d || '') ? d : ''; }

  /* Today in UTC as YYYY-MM-DD, so it compares as a string against Best Buy's
   * own date shape and never drifts a day from a local clock. */
  function todayUTC() {
    var n = new Date();
    function p(x) { return (x < 10 ? '0' : '') + x; }
    return n.getUTCFullYear() + '-' + p(n.getUTCMonth() + 1) + '-' + p(n.getUTCDate());
  }

  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /*
   * "2026-09-26" -> "Sat, Sep 26". The third copy of this, and it must agree
   * character for character with dayLabel() in improvemint-delivery.js and with
   * snippets/improvemint-delivery-window-label.liquid, or the alert and the
   * banner name different days for the same cart.
   *
   * Parsed and formatted from UTC parts: `new Date('2026-09-26')` is midnight
   * UTC and every local getter west of Greenwich reads it as the 25th, which is
   * the one number a shopper is held to.
   */
  function dayLabel(date) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
    if (!m) return date || '';
    var d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    if (isNaN(d.getTime())) return date;
    return DAYS[d.getUTCDay()] + ', ' + MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate();
  }

  /* Up to three names, then a count. Mirrors nameList() in
   * improvemint-cart-fulfillment.js so the two alerts name products alike. */
  function nameList(names) {
    var all = names.filter(Boolean);
    var shown = all.slice(0, NAMES_SHOWN);
    var rest = all.length - shown.length;
    if (rest > 0) return shown.join(', ') + ' and ' + rest + ' more item' + (rest === 1 ? '' : 's');
    if (shown.length <= 1) return shown[0] || '';
    return shown.slice(0, -1).join(', ') + ' and ' + shown[shown.length - 1];
  }

  function fill(text, map) {
    return String(text).replace(/\{(\w+)\}/g, function (whole, key) {
      return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : whole;
    });
  }

  /*
   * Does this answer say Best Buy will only bring the SKU on its own truck? Not
   * parcel-shipped (`shipping: false`), and home-delivered — the dishwasher's
   * shape: `homeDelivery: true, shipping: false`. Pickup is a separate question,
   * asked against the cart's store below: a SKU like this can still be
   * collectable (sku 6673091 answers pickup true), and then a pickup cart is fine.
   */
  function truckOnlyAnswer(d) {
    var del = d && d.delivery;
    return !!del && del.shipping === false &&
      (del.homeDelivery === true || del.fulfillment === 'home-delivery');
  }

  /*
   * Does this answer say Best Buy will only hand the SKU over at a store
   * (2026-10-27)? A real `pickup` and no `delivery` at all — `delivery` is null
   * "only when Best Buy will neither ship nor deliver the SKU"
   * (docs/COMMERCE-PDP-PICKER.md §3). 6351275 and 6506838 answer exactly that.
   *
   * Only ever true of a real answer: `unsellable` is its own verdict, a body
   * carrying `error` is no answer, and `pickup: null` + `delivery: null` is the
   * degraded shape — it says nothing about pickup, so it is never read as this
   * (the same rule that keeps `unsellable` from being inferred from it).
   */
  function pickupOnlyAnswer(d) {
    return !!d && !d.error && d.unsellable !== true && !!d.pickup && !d.delivery;
  }

  /*
   * Nothing can bring this SKU to THIS shopper (2026-10-28). Best Buy was asked
   * about the cart's ZIP and named no store that has it (`pickup: null` with
   * `pickupUnavailableReason: "no-stores-near-zip"`, bestbuy-sync #648), and it
   * has no delivery route at all. GP found the shape on 6576999 at 33401, where
   * bestbuy.com reads "Unavailable".
   *
   * Read off the explicit reason, never inferred from `pickup: null` +
   * `delivery: null`, which is also the degraded no-answer shape. Not
   * `unsellable`: that verdict is about the SKU everywhere, this one is about
   * one ZIP, so the PDP keeps selling it to everyone else.
   */
  function noRouteNearZipAnswer(d) {
    return !!d && !d.error && d.unsellable !== true && !d.pickup && !d.delivery &&
      d.pickupUnavailableReason === 'no-stores-near-zip';
  }

  /*
   * Everything we can say about this cart, from the answers we actually got.
   *
   * `complete` is true only when every line was answered. It decides whether the
   * verdict may be cached, never whether a finding is acted on: a finding stands
   * on its own SKU's answer, and a SKU we could not reach simply produces none —
   * with ONE exception: a line with no answer whose product carries
   * `bb-delivery:home` still counts as truck-only (see the header).
   *
   * ── THE TRUCK-ONLY BLOCK (2026-10-17) ────────────────────────────────────
   * A line is SERVED by the cart when its one fulfilment can actually carry it:
   *
   *   home delivery, complete window   every truck-only line is served
   *   store pickup at a store          served when the answer offers pickup at
   *                                    that store; an unanswered line counts as
   *                                    served, because no answer means we cannot
   *                                    see the store, and this file fails open
   *   anything else                    `delivery`, blank, an incomplete window, a
   *                                    pickup with no store: never served — it
   *                                    places as ship-to-home, which Best Buy will
   *                                    not do for this SKU
   *
   * Then: an unserved truck-only line in a cart with no other Best Buy line →
   * block, link to its page to choose a date. A truck-only line beside a Best Buy
   * line that is NOT truck-only, where the cart's choice cannot serve both (an
   * unserved truck line, or a home-delivery cart carrying a parcel / pickup item)
   * → block as a mixed cart, naming both. Neither is rewritten; the shopper
   * changes the cart. What a mixed cart should do instead is open — GP deciding.
   *
   * ── THE PICKUP-ONLY BLOCK (2026-10-27) ───────────────────────────────────
   * The same idea from the other side. A pickup-only line (pickupOnlyAnswer) is
   * SERVED only by store pickup at a store: the cart is on `store-pickup` with a
   * store id, and that store is in the answer's `pickup.stores` — or the answer
   * has no `stores` array, the fail-open convention of the store check below.
   * Every other cart places it as ship-to-home, which Best Buy refuses.
   *
   * Then: a pickup-only line beside a Best Buy line that has a delivery route
   * and NO pickup (`noPickup` — it must ship), where no cart-level choice can
   * carry both whatever the cart says → block as a mixed cart, naming both, in
   * the truck-only mixed message's shape. Otherwise an unserved pickup-only line
   * → block, link to its page to choose a store. A pickup-only line stays in
   * `others`, so the truck-only checks above see it exactly as before; when the
   * truck-only MIXED block fires it already names the pickup-only line, and the
   * single pickup-only block is not added beside it. A truck-only line is never
   * counted in `noPickup` for the same reason. Unanswered lines are in neither
   * list: this block stands on answers only.
   */
  function assess(banner, items, answers, cart) {
    var unsellable = [];
    var noRoute = [];           // answered for this ZIP: no store has it and nothing delivers it (2026-10-28)
    var pickupGone = [];
    var truck = [];             // { item, served } — lines only Best Buy's truck brings
    var others = [];            // every other Best Buy line, pickup-only ones included
    var pickupOnly = [];        // { item, served } — lines only a store hands over (2026-10-27)
    var noPickup = [];          // answered, not truck-only: a delivery route and no pickup
    var complete = true;
    var canShipAll = true;
    var freshEarliest = '';
    var hdBooked = cart.choice === 'home-delivery' && !!validDate(cart.hdDate) && !!cart.hdStart;
    var onPickup = cart.choice === 'store-pickup' && !!cart.storeId;

    items.forEach(function (item) {
      var d = answers[item.sku];
      if (!d || d.error) {
        complete = false;
        canShipAll = false;
        // No answer: the product tag says which kind of line this is.
        if (item.home) truck.push({ item: item, served: hdBooked || onPickup });
        else others.push(item);
        return;
      }

      if (d.unsellable === true) {
        unsellable.push(item);
        canShipAll = false;
        return;   // nothing else is worth saying about a line that must come out
      }

      if (noRouteNearZipAnswer(d)) {
        // The same: no other store, cart choice or date can carry it here.
        noRoute.push(item);
        canShipAll = false;
        return;
      }

      // "Switch to shipping" is offered only when every line can actually SHIP
      // (2026-10-17). `delivery` alone also means "Best Buy's truck", so a
      // truck-only line kept it true and could be offered a parcel switch.
      if (!d.delivery || d.delivery.shipping === false) canShipAll = false;

      var gone = false;
      if (onPickup) {
        if (!d.pickup) {
          // Best Buy will not let this be collected at all any more. The
          // downgrade check (§8) would reach the same conclusion, from a
          // different call; getting there first on the click is free.
          gone = true;
        } else if (Object.prototype.toString.call(d.pickup.stores) === '[object Array]'
                   && d.pickup.stores.length) {
          var here = false;
          for (var i = 0; i < d.pickup.stores.length; i++) {
            if (String(d.pickup.stores[i] && d.pickup.stores[i].id) === cart.storeId) { here = true; break; }
          }
          if (!here) gone = true;
        }
        // No `stores` array at all is NOT a finding: that is what the answer
        // looks like without a ZIP, and it says nothing about this store.
      }

      if (truckOnlyAnswer(d)) {
        // Said by the truck-only block below, not as a store that "no longer"
        // has it — for this SKU the store question was never the problem.
        truck.push({ item: item, served: hdBooked || (onPickup && !gone) });
      } else {
        others.push(item);
        if (pickupOnlyAnswer(d)) {
          // Said by the pickup-only block below, not as a store HOLD (2026-10-27):
          // a hold lets the second click through, and with no delivery route a
          // store is the only way this line can come — another cart choice
          // cannot rescue it, only another store.
          pickupOnly.push({ item: item, served: onPickup && !gone });
        } else {
          if (gone) pickupGone.push(item);
          // `delivery` is required, so the degraded both-null shape is never
          // read as "cannot be picked up" (the same rule as pickupOnlyAnswer).
          if (!d.pickup && d.delivery) noPickup.push(item);
        }
      }

      if (cart.choice === 'home-delivery') {
        var e = validDate(d.delivery && d.delivery.earliestDate);
        // The LATEST per-SKU earliest is the soonest the whole cart can go.
        if (e && e > freshEarliest) freshEarliest = e;
      }
    });

    if (items.length > MAX_SKUS) complete = false;

    var findings = [];

    if (unsellable.length) {
      findings.push({
        severity: 'block',
        text: fill(label(banner, 'unsellable',
          '{items} is no longer available to order. Please remove it from your cart to check out.'),
          { items: nameList(unsellable.map(function (i) { return i.title; })) })
      });
    }

    if (noRoute.length) {
      findings.push({
        severity: 'block',
        cartLead: true,
        text: fill(label(banner, 'no-route',
          '{items} isn’t available in your area right now: no nearby Best Buy store has it, and it can’t be shipped or delivered. Please remove it from your cart to check out.'),
          { items: nameList(noRoute.map(function (i) { return i.title; })) })
      });
    }

    var unserved = truck.filter(function (t) { return !t.served; }).map(function (t) { return t.item; });
    var title = function (i) { return i.title; };

    var truckMixed = !!(truck.length && others.length && (unserved.length || hdBooked));

    if (truckMixed) {
      findings.push({
        severity: 'block',
        cartLead: true,
        text: fill(label(banner, 'mixed',
          '{items} comes by Best Buy home delivery and can’t be in the same order as {others}, which ship or are picked up. Remove one or the other to check out.'),
          {
            items: nameList((unserved.length ? unserved : truck.map(function (t) { return t.item; })).map(title)),
            others: nameList(others.map(title))
          })
      });
    } else if (unserved.length) {
      findings.push({
        severity: 'block',
        cartLead: true,
        text: fill(label(banner, 'truck',
          '{items} comes by Best Buy home delivery only, so it can’t be shipped or picked up. Choose a delivery date on its page to check out.'),
          { items: nameList(unserved.map(title)) }),
        links: unserved.map(function (i) {
          return { href: i.url, text: fill(label(banner, 'truck-link', 'Choose a delivery date for {item}'), { item: i.title }) };
        })
      });
    }

    // The pickup-only block (2026-10-27): see the comment above assess().
    var pickupItems = pickupOnly.map(function (p) { return p.item; });
    var pickupUnserved = pickupOnly.filter(function (p) { return !p.served; }).map(function (p) { return p.item; });

    if (pickupItems.length && noPickup.length) {
      // Every pickup-only line is named, served or not: none of them can share
      // an order with a line that cannot be picked up, whatever the cart says.
      findings.push({
        severity: 'block',
        cartLead: true,
        text: fill(label(banner, 'pickup-mixed',
          '{items} is available for Best Buy store pickup only and can’t be in the same order as {others}, which can’t be picked up. Remove one or the other to check out.'),
          {
            items: nameList(pickupItems.map(title)),
            others: nameList(noPickup.map(title))
          })
      });
    } else if (pickupUnserved.length && !truckMixed) {
      findings.push({
        severity: 'block',
        cartLead: true,
        text: fill(label(banner, 'pickup',
          '{items} is available for Best Buy store pickup only, so it can’t be shipped or delivered. Choose a pickup store on its page to check out.'),
          { items: nameList(pickupUnserved.map(title)) }),
        links: pickupUnserved.map(function (i) {
          return { href: i.url, text: fill(label(banner, 'pickup-link', 'Choose a pickup store for {item}'), { item: i.title }) };
        })
      });
    }

    if (pickupGone.length) {
      findings.push({
        severity: 'hold',
        text: fill(label(banner, 'store', '{store} no longer has {items} available for pickup.'), {
          store: cart.storeName || '',
          items: nameList(pickupGone.map(function (i) { return i.title; }))
        })
      });
    }

    if (cart.choice === 'home-delivery' && validDate(cart.hdDate)) {
      if (freshEarliest && freshEarliest !== cart.hdDate) {
        findings.push({
          severity: 'info',
          text: fill(label(banner, 'delivery', 'The earliest delivery date for your cart is now {date}.'),
            { date: dayLabel(freshEarliest) })
        });
      } else if (!freshEarliest && cart.hdDate < todayUTC()) {
        // No new date to name, and the one in the cart is in the past. Placement
        // fails that outright (DELIVERY_DATE_PASSED), so it has to be said.
        findings.push({
          severity: 'info',
          text: label(banner, 'delivery-passed',
            'The delivery date saved with your cart has passed. A new date is confirmed when your order is placed.')
        });
      }
    }

    // Costs no Best Buy call and survives a total outage: purely the shopper's
    // own ZIP against the one the cart was quoted at.
    var zip = currentZip();
    if (zip && cart.quotedZip && zip !== cart.quotedZip) {
      findings.push({
        severity: 'info',
        text: fill(label(banner, 'zip',
          'Your cart was quoted for ZIP {cartzip}. You have since changed your ZIP to {zip}.'),
          { cartzip: cart.quotedZip, zip: zip })
      });
    }

    return { findings: findings, complete: complete, canShipAll: canShipAll && items.length > 0 };
  }

  function worst(findings) {
    var rank = { info: 1, hold: 2, block: 3 };
    var out = '';
    findings.forEach(function (f) { if (!out || rank[f.severity] > rank[out]) out = f.severity; });
    return out;
  }

  /* --------------------------------------------------------------- painting */

  function paint(verdict, focusOn) {
    banners().forEach(function (banner) {
      var box = banner.querySelector('[data-im-cart-recheck]');
      if (!box) return;
      var lead = box.querySelector('[data-im-cart-recheck-lead]');
      var list = box.querySelector('[data-im-cart-recheck-list]');
      var action = box.querySelector('[data-im-cart-recheck-action]');

      if (!verdict || !verdict.findings.length) {
        if (lead) lead.textContent = '';
        if (list) list.textContent = '';
        if (action) { action.hidden = true; action.textContent = ''; }
        box.hidden = true;
        box.removeAttribute('data-severity');
        return;
      }

      var level = worst(verdict.findings);
      box.setAttribute('data-severity', level);
      if (lead) {
        /*
         * The truck-only and mixed-cart blocks (2026-10-17), and the pickup-only
         * ones (2026-10-27), are about what is IN the cart, not about something
         * that changed while it sat there, and they stop checkout — so "Some
         * details changed … then check out" read as a contradiction on the
         * preview QA. They take their own opening line.
         */
        var cartLead = verdict.findings.some(function (f) { return f.cartLead; });
        lead.textContent = cartLead
          ? label(banner, 'lead-block', 'This cart can’t go to checkout as it is.')
          : label(banner, 'lead',
            'Some details changed while this was in your cart. Please review them, then check out.');
      }
      if (list) {
        list.textContent = '';
        verdict.findings.forEach(function (f) {
          var li = document.createElement('li');
          li.className = 'im-cart-recheck__item';
          li.textContent = f.text;
          list.appendChild(li);
          /*
           * The way to fix a truck-only line is on its own product page
           * (2026-10-17), so it gets a link there — and so does a pickup-only
           * line (2026-10-27), whose store is chosen there. Nodes, not
           * innerHTML, and only a same-site path — the href is Liquid's
           * `item.url`.
           */
          (f.links || []).forEach(function (l) {
            if (!l || !/^\/(?!\/)/.test(l.href || '')) return;
            var sub = document.createElement('li');
            sub.className = 'im-cart-recheck__item im-cart-recheck__item--sub';
            var a = document.createElement('a');
            a.className = 'im-cart-recheck__link';
            a.href = l.href;
            a.textContent = l.text;
            sub.appendChild(a);
            list.appendChild(sub);
          });
        });
        /*
         * The way out, said whenever the store finding is the worst of them —
         * NOT gated on the button, because a pickup-only SKU gets no button and
         * would otherwise be left as a statement with no action beside it. The
         * button, when it is there, labels itself. (Since 2026-10-27 that is a
         * pickup-only line the store DOES have, beside another line it lacks: a
         * pickup-only line at a store that lacks it is a block, not a hold.)
         */
        if (level === 'hold') {
          var hint = document.createElement('li');
          hint.className = 'im-cart-recheck__item im-cart-recheck__item--sub';
          hint.textContent = label(banner, 'store-hint',
            'Pick a different store on the product page to keep this as a pickup.');
          list.appendChild(hint);
        }
      }
      if (action) {
        action.hidden = !verdict.offerShipping;
        action.textContent = verdict.offerShipping
          ? label(banner, 'switch', 'Switch to shipping')
          : '';
      }
      box.hidden = false;

      if (focusOn === banner && box.focus) {
        try { box.focus({ preventScroll: false }); } catch (e) { box.focus(); }
      }
    });
  }

  function setBusy(submitter, banner, busy) {
    if (!submitter) return;
    var text = submitter.querySelector ? submitter.querySelector('.btn__text') : null;
    if (busy) {
      if (submitter.__imLabel == null) {
        submitter.__imLabel = text ? text.textContent : submitter.textContent;
      }
      var msg = label(banner, 'checking', 'Checking availability…');
      if (text) text.textContent = msg; else submitter.textContent = msg;
      submitter.setAttribute('aria-busy', 'true');
    } else {
      if (submitter.__imLabel != null) {
        if (text) text.textContent = submitter.__imLabel; else submitter.textContent = submitter.__imLabel;
        submitter.__imLabel = null;
      }
      submitter.removeAttribute('aria-busy');
    }
  }

  /* ---------------------------------------------------------- cart write */

  /*
   * The eleven-key ground-shipping write lives in
   * assets/improvemint-cart-fulfillment.js and is borrowed, not copied.
   *
   * §6 of docs/COMMERCE-PDP-PICKER.md is explicit that every cart write carries
   * ALL eleven bb_* keys, the inapplicable ones as "", because a subset write
   * leaves a cart holding half a pickup beside half a delivery. There were
   * already three copies of that list in this theme; a fourth here would be the
   * one most likely to drift, since it is the only one not beside a picker. Both
   * files are `defer` and that one is loaded first (layout/theme.liquid), so the
   * hook is always there by the time a shopper can press anything.
   */
  function switchToShipping() {
    var api = window.imCartFulfillment;
    if (!api || typeof api.switchToShipping !== 'function') return Promise.resolve();
    // Always a settled promise: the caller re-enables the button and repaints in
    // .then(), and a thrown write must not strand it mid-action. The cart write
    // itself is already non-fatal over there, and placement re-checks every SKU
    // independently whatever the theme shows.
    try { return Promise.resolve(api.switchToShipping()); }
    catch (e) { return Promise.resolve(); }
  }

  /* ---------------------------------------------------------------- wiring */

  /*
   * Hyper's cart page posts `<form id="cart">` and the drawer posts its own
   * footer form, both with `<button type="submit" name="checkout">`; Shopify
   * routes to checkout off that name. So the hook is `submit` on `document` in
   * the CAPTURE phase, filtered to that submitter — the same idiom, and the same
   * reason, as the add-to-cart interception in improvemint-pickup.js. A `click`
   * listener would also catch a keyboard Enter on some browsers and not others;
   * `submit` catches every route to the same POST.
   *
   * The replay carries the submitter through requestSubmit(), because
   * form.submit() drops the button's name and the POST would then be an ordinary
   * cart update rather than a checkout.
   */
  var replaying = false;
  var announced = {};        // signature -> true, once the shopper has read it
  var blocking = {};         // signature -> true; clears only when the cart changes
  var lastVerdict = {};      // signature -> the verdict we painted, complete or not

  function isCheckoutSubmitter(el) {
    return !!(el && el.getAttribute && el.getAttribute('name') === 'checkout');
  }

  document.addEventListener('submit', function (e) {
    if (replaying) return;

    var form = e.target;
    if (!form || !form.querySelector) return;

    var submitter = e.submitter || null;
    if (!isCheckoutSubmitter(submitter)) {
      // Safari before 15.4 has no event.submitter. The focused control is the
      // one that was pressed on every route that reaches here.
      var active = document.activeElement;
      submitter = isCheckoutSubmitter(active) ? active : null;
      if (!submitter) return;
    }

    var list = banners();
    if (!list.length) return;                          // no Best Buy line: nothing to re-check

    // The drawer is global, so a cart page has BOTH banners. Speak in the one
    // the shopper is looking at; paint() writes to both so they never disagree.
    var inDrawer = !!(submitter.closest && submitter.closest('cart-drawer'));
    var banner = null;
    for (var i = 0; i < list.length; i++) {
      var isDrawerBanner = list[i].getAttribute('data-im-cart-context') === 'drawer';
      if (isDrawerBanner === inDrawer) { banner = list[i]; break; }
    }
    if (!banner) banner = list[0];

    var items = lines(banner);
    if (!items.length) return;

    var cart = cartState(banner);
    var sig = signature(items, cart);

    /*
     * The replay, defined before anything can need it. requestSubmit carries the
     * submitter, and the submitter's `name="checkout"` is what makes Shopify
     * route this POST to checkout rather than treat it as a cart update --
     * form.submit() drops it, so it is only ever the last resort.
     */
    /*
     * ALWAYS IN A NEW TASK (fixed 2026-10-17). When every answer is already in
     * the cache — the usual case for a shopper who came from the product page —
     * lookupAll() settles through microtasks alone, and the browser runs
     * microtasks BETWEEN the listeners of the very submit event we are holding.
     * The replay then called requestSubmit() while that event was still being
     * dispatched, and the HTML spec drops a submission made while the form is
     * "firing submission events" — silently. So the first Check Out press on a
     * clean cart did nothing at all, and only a second press (which goes through
     * `announced`) reached checkout. Found in the preview QA of PR #288: no
     * submit event followed the replay's requestSubmit(). A zero-delay timer runs
     * after the dispatch has finished, where the replay is a real submission.
     */
    function replay() {
      setTimeout(function () {
        replaying = true;
        try {
          try {
            if (form.requestSubmit) form.requestSubmit(submitter);
            else form.submit();
          } catch (err) {
            if (form.requestSubmit) form.requestSubmit();
            else form.submit();
          }
        } finally {
          replaying = false;
        }
      }, 0);
    }

    if (blocking[sig]) {
      // An unsellable line is still in the cart. Refuse again, and re-show why:
      // the shopper may well have scrolled past the alert to press the button.
      e.preventDefault();
      e.stopPropagation();
      paint(lastVerdict[sig] || null, banner);
      return;
    }

    if (announced[sig]) return;                        // told once, let them through

    var cached = verdictGet(sig);
    if (cached && !cached.findings.length) return;     // known clean: no delay, no call
    if (cached && cached.findings.length) {
      e.preventDefault();
      e.stopPropagation();
      applyVerdict(sig, cached, banner, replay);
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setBusy(submitter, banner, true);

    var deadlineAt = Date.now() + DEADLINE_MS;
    var finished = false;

    function done(verdict) {
      if (finished) return;
      finished = true;
      setBusy(submitter, banner, false);
      applyVerdict(sig, verdict, banner, replay);
    }

    // The button can never be stranded. Whichever fires first wins, and a null
    // verdict means "we learned nothing", which lets the click through.
    setTimeout(function () { done(null); }, REPLAY_AFTER_MS);

    lookupAll(items, cart.quotedZip, deadlineAt).then(function (answers) {
      var verdict = assess(banner, items, answers, cart);
      // The one-click way out is offered only when we KNOW every line can ship.
      // An unknown line makes canShipAll false, so an outage never offers it.
      verdict.offerShipping = verdict.canShipAll && worst(verdict.findings) === 'hold';
      // Only a complete verdict may be remembered: one timeout must not silence
      // the check, nor excuse it, for the next ten minutes.
      if (verdict.complete) verdictSet(sig, verdict);
      done(verdict);
    }, function () { done(null); });
  }, true);

  function applyVerdict(sig, verdict, banner, replay) {
    lastVerdict[sig] = verdict || null;

    if (!verdict || !verdict.findings.length) {
      paint(null, null);
      announced[sig] = true;
      replay();
      return;
    }

    paint(verdict, banner);

    if (worst(verdict.findings) === 'block') {
      // Nothing can fulfil that line, so there is no "proceed anyway". The way
      // out is removing it, which changes the signature and reopens the question
      // from scratch.
      blocking[sig] = true;
      return;
    }

    // hold and info alike: the shopper has now been told, so the next press goes
    // to checkout. A hold also carries a one-click way to make the cart placeable.
    announced[sig] = true;

    var box = banner.querySelector('[data-im-cart-recheck]');
    var action = box && box.querySelector('[data-im-cart-recheck-action]');
    if (!action || action.hidden || action.__imWired) return;
    action.__imWired = true;
    action.addEventListener('click', function () {
      action.disabled = true;
      switchToShipping().then(function () {
        /*
         * Repaint through the event both pickers already dispatch, so the banner,
         * the per-line notes and any downgrade message move together and this
         * file owns none of that rendering. The cart attributes are what changed;
         * the next server render of the cart is the authority, as always.
         */
        document.dispatchEvent(new CustomEvent('im:cart-fulfillment-change', {
          detail: { state: 'delivery', value: '' }
        }));
        paint(null, null);
        action.disabled = false;
      });
    });
  }
})();
